import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { pageRoutes } from '@/lib/pageRoutes';
import { Layout } from '@/components/Layout';
import { NotFound } from '@/components/NotFound';
import ProtectedRoute from '@/components/ProtectedRoute';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import LoginPage from './components/auth/LoginPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';

const RoleBasedRedirect = () => {
  const { userProfile } = useAuth();
  const userRole = userProfile?.role?.toLowerCase();

  if (userRole === 'transporter') {
    return <Navigate to="/live-tracking" replace />;
  } else if (userRole === 'distributor' || userRole === 'retailer') {
    return <Navigate to="/trip-management" replace />;
  } else if (['region_head', 'account_head', 'field_officer'].includes(userRole)) {
    return <Navigate to="/rake-receipts" replace />;
  } else if (userRole === 'account_officer') {
    return <Navigate to="/correction-tickets" replace />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const DashboardRoute = () => {
  const { userProfile } = useAuth();
  const userRole = userProfile?.role?.toLowerCase();

  if (userRole === 'transporter') {
    return <Navigate to="/live-tracking" replace />;
  } else if (userRole === 'distributor' || userRole === 'retailer') {
    return <Navigate to="/trip-management" replace />;
  } else if (['region_head', 'account_head', 'field_officer'].includes(userRole)) {
    return <Navigate to="/rake-receipts" replace />;
  } else if (userRole === 'account_officer') {
    return <Navigate to="/correction-tickets" replace />;
  }

  return (
    <Layout>
      <pageRoutes.dashboard.component />
    </Layout>
  );
};

function AppContent() {
  return (
    <HelmetProvider>
      <div className="App">
        <Helmet>
          <title>IPL - VLTS Platform</title>
          <meta name="description" content=" dashboard for IPL - Fleet Tracking management with advanced features and analytics" />
        </Helmet>

        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route path="/" element={
            <ProtectedRoute>
              <RoleBasedRedirect />
            </ProtectedRoute>
          } />

          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardRoute />
            </ProtectedRoute>
          } />

          {Object.entries(pageRoutes).map(([route, config]) => {
            if (route === 'dashboard') return null;
            return (
              <Route
                key={route}
                path={`/${route}`}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <config.component />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            );
          })}

          <Route path="*" element={<NotFound />} />
        </Routes>

        <Toaster />
      </div>
    </HelmetProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;